import type { RouteKey } from '@/i18n/routes'

export type TechnologyRouteKey = Extract<RouteKey,
  | 'm-hydro-5d'
  | 'm-laser-pro'
  | 'm-body-l'
  | 'm-synergy-a'
  | 'm-body-s'
  | 'm-press'
  | 'm-shock'
  | 'm-rf-synergy'
>

export interface TechnologyMedia {
  src: string
  width: number
  height: number
  altKey: string
}

export interface TechnologyCopyBlock {
  titleKey: string
  bodyKeys: readonly string[]
}

export interface TechnologyListSection extends TechnologyCopyBlock {
  itemKeys: readonly string[]
}

export interface TechnologySpecification {
  labelKey: string
  valueKey: string
}

/**
 * Data contract consumed by the shared detail-page template from assignment 05.
 * Optional sections are omitted when the source does not provide reliable data;
 * empty blocks and placeholder specifications are not valid substitutes.
 */
export interface TechnologyPageRecord {
  route: TechnologyRouteKey
  image: TechnologyMedia
  hero: TechnologyCopyBlock
  introduction: TechnologyCopyBlock
  applications?: TechnologyListSection
  operation?: TechnologyCopyBlock
  specifications?: readonly TechnologySpecification[]
  support?: TechnologyCopyBlock
  related?: readonly TechnologyRouteKey[]
  contactInterest: string
}

export interface TechnologyCatalogItem {
  route: TechnologyRouteKey
  image: Omit<TechnologyMedia, 'altKey'>
}

export const technologyCatalog = [
  { route: 'm-hydro-5d', image: { src: '/media/technologies/m-hydro-5d.webp', width: 480, height: 1249 } },
  { route: 'm-laser-pro', image: { src: '/media/technologies/m-laser-pro.webp', width: 480, height: 1267 } },
  { route: 'm-body-l', image: { src: '/media/technologies/m-body-l.webp', width: 480, height: 952 } },
  { route: 'm-synergy-a', image: { src: '/media/technologies/m-synergy-a.webp', width: 480, height: 1099 } },
  { route: 'm-body-s', image: { src: '/media/technologies/m-body-s.webp', width: 480, height: 970 } },
  { route: 'm-press', image: { src: '/media/technologies/m-press.webp', width: 480, height: 1115 } },
  { route: 'm-shock', image: { src: '/media/technologies/m-shock.webp', width: 480, height: 1080 } },
  { route: 'm-rf-synergy', image: { src: '/media/technologies/m-rf-synergy.webp', width: 480, height: 1122 } },
] as const satisfies readonly TechnologyCatalogItem[]

export const technologyKeys = technologyCatalog.map(item => item.route)

export const mHydro5d: TechnologyPageRecord = {
  route: 'm-hydro-5d',
  image: {
    src: '/media/technologies/m-hydro-5d.webp',
    width: 480,
    height: 1249,
    altKey: 'technology.m-hydro-5d.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-hydro-5d.hero.title',
    bodyKeys: ['technology.m-hydro-5d.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-hydro-5d.introduction.title',
    bodyKeys: [
      'technology.m-hydro-5d.introduction.bodyOne',
      'technology.m-hydro-5d.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-hydro-5d.applications.title',
    bodyKeys: ['technology.m-hydro-5d.applications.body'],
    itemKeys: [
      'technology.m-hydro-5d.applications.cleansing',
      'technology.m-hydro-5d.applications.hydration',
      'technology.m-hydro-5d.applications.texture',
      'technology.m-hydro-5d.applications.tone',
    ],
  },
  operation: {
    titleKey: 'technology.m-hydro-5d.operation.title',
    bodyKeys: [
      'technology.m-hydro-5d.operation.bodyOne',
      'technology.m-hydro-5d.operation.bodyTwo',
    ],
  },
  specifications: [
    {
      labelKey: 'technology.m-hydro-5d.specifications.hydrogenGeneratorLabel',
      valueKey: 'technology.m-hydro-5d.specifications.hydrogenGeneratorValue',
    },
    {
      labelKey: 'technology.m-hydro-5d.specifications.touchscreenLabel',
      valueKey: 'technology.m-hydro-5d.specifications.touchscreenValue',
    },
    {
      labelKey: 'technology.m-hydro-5d.specifications.programsLabel',
      valueKey: 'technology.m-hydro-5d.specifications.programsValue',
    },
    {
      labelKey: 'technology.m-hydro-5d.specifications.vacuumLabel',
      valueKey: 'technology.m-hydro-5d.specifications.vacuumValue',
    },
    {
      labelKey: 'technology.m-hydro-5d.specifications.finishLabel',
      valueKey: 'technology.m-hydro-5d.specifications.finishValue',
    },
  ],
  support: {
    titleKey: 'technology.m-hydro-5d.support.title',
    bodyKeys: ['technology.m-hydro-5d.support.body'],
  },
  contactInterest: 'm-hydro-5d',
}

export const mLaserPro: TechnologyPageRecord = {
  route: 'm-laser-pro',
  image: {
    src: '/media/technologies/m-laser-pro.webp',
    width: 480,
    height: 1267,
    altKey: 'technology.m-laser-pro.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-laser-pro.hero.title',
    bodyKeys: ['technology.m-laser-pro.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-laser-pro.introduction.title',
    bodyKeys: [
      'technology.m-laser-pro.introduction.bodyOne',
      'technology.m-laser-pro.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-laser-pro.applications.title',
    bodyKeys: ['technology.m-laser-pro.applications.body'],
    itemKeys: ['technology.m-laser-pro.applications.hairRemoval'],
  },
  operation: {
    titleKey: 'technology.m-laser-pro.operation.title',
    bodyKeys: [
      'technology.m-laser-pro.operation.bodyOne',
      'technology.m-laser-pro.operation.bodyTwo',
    ],
  },
  specifications: [
    { labelKey: 'technology.m-laser-pro.specifications.powerLabel', valueKey: 'technology.m-laser-pro.specifications.powerValue' },
    { labelKey: 'technology.m-laser-pro.specifications.treatmentAreaLabel', valueKey: 'technology.m-laser-pro.specifications.treatmentAreaValue' },
    { labelKey: 'technology.m-laser-pro.specifications.diodesLabel', valueKey: 'technology.m-laser-pro.specifications.diodesValue' },
    { labelKey: 'technology.m-laser-pro.specifications.frequencyLabel', valueKey: 'technology.m-laser-pro.specifications.frequencyValue' },
    { labelKey: 'technology.m-laser-pro.specifications.wavelengthLabel', valueKey: 'technology.m-laser-pro.specifications.wavelengthValue' },
    { labelKey: 'technology.m-laser-pro.specifications.displayLabel', valueKey: 'technology.m-laser-pro.specifications.displayValue' },
  ],
  contactInterest: 'm-laser-pro',
}

export const mBodyL: TechnologyPageRecord = {
  route: 'm-body-l',
  image: {
    src: '/media/technologies/m-body-l.webp',
    width: 480,
    height: 952,
    altKey: 'technology.m-body-l.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-body-l.hero.title',
    bodyKeys: ['technology.m-body-l.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-body-l.introduction.title',
    bodyKeys: [
      'technology.m-body-l.introduction.bodyOne',
      'technology.m-body-l.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-body-l.applications.title',
    bodyKeys: ['technology.m-body-l.applications.body'],
    itemKeys: [
      'technology.m-body-l.applications.localizedAdiposity',
      'technology.m-body-l.applications.celluliteAppearance',
    ],
  },
  operation: {
    titleKey: 'technology.m-body-l.operation.title',
    bodyKeys: [
      'technology.m-body-l.operation.bodyOne',
      'technology.m-body-l.operation.bodyTwo',
    ],
  },
  specifications: [
    { labelKey: 'technology.m-body-l.specifications.laserLabel', valueKey: 'technology.m-body-l.specifications.laserValue' },
    { labelKey: 'technology.m-body-l.specifications.infraredLabel', valueKey: 'technology.m-body-l.specifications.infraredValue' },
    { labelKey: 'technology.m-body-l.specifications.padsLabel', valueKey: 'technology.m-body-l.specifications.padsValue' },
    { labelKey: 'technology.m-body-l.specifications.durationLabel', valueKey: 'technology.m-body-l.specifications.durationValue' },
    { labelKey: 'technology.m-body-l.specifications.finishLabel', valueKey: 'technology.m-body-l.specifications.finishValue' },
  ],
  related: ['m-body-s', 'm-press'],
  contactInterest: 'm-body-l',
}

export const mBodyS: TechnologyPageRecord = {
  route: 'm-body-s',
  image: {
    src: '/media/technologies/m-body-s.webp',
    width: 480,
    height: 970,
    altKey: 'technology.m-body-s.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-body-s.hero.title',
    bodyKeys: ['technology.m-body-s.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-body-s.introduction.title',
    bodyKeys: [
      'technology.m-body-s.introduction.bodyOne',
      'technology.m-body-s.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-body-s.applications.title',
    bodyKeys: ['technology.m-body-s.applications.body'],
    itemKeys: [
      'technology.m-body-s.applications.bodyShaping',
      'technology.m-body-s.applications.firming',
      'technology.m-body-s.applications.drainage',
    ],
  },
  operation: {
    titleKey: 'technology.m-body-s.operation.title',
    bodyKeys: [
      'technology.m-body-s.operation.bodyOne',
      'technology.m-body-s.operation.bodyTwo',
    ],
  },
  specifications: [
    { labelKey: 'technology.m-body-s.specifications.powerLabel', valueKey: 'technology.m-body-s.specifications.powerValue' },
    { labelKey: 'technology.m-body-s.specifications.temperatureLabel', valueKey: 'technology.m-body-s.specifications.temperatureValue' },
    { labelKey: 'technology.m-body-s.specifications.controlLabel', valueKey: 'technology.m-body-s.specifications.controlValue' },
    { labelKey: 'technology.m-body-s.specifications.displayLabel', valueKey: 'technology.m-body-s.specifications.displayValue' },
    { labelKey: 'technology.m-body-s.specifications.marsupioLabel', valueKey: 'technology.m-body-s.specifications.marsupioValue' },
    { labelKey: 'technology.m-body-s.specifications.durationLabel', valueKey: 'technology.m-body-s.specifications.durationValue' },
  ],
  related: ['m-body-l', 'm-rf-synergy'],
  contactInterest: 'm-body-s',
}

export const mPress: TechnologyPageRecord = {
  route: 'm-press',
  image: {
    src: '/media/technologies/m-press.webp',
    width: 480,
    height: 1115,
    altKey: 'technology.m-press.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-press.hero.title',
    bodyKeys: ['technology.m-press.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-press.introduction.title',
    bodyKeys: [
      'technology.m-press.introduction.bodyOne',
      'technology.m-press.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-press.applications.title',
    bodyKeys: ['technology.m-press.applications.body'],
    itemKeys: [
      'technology.m-press.applications.drainage',
      'technology.m-press.applications.silhouette',
    ],
  },
  operation: {
    titleKey: 'technology.m-press.operation.title',
    bodyKeys: [
      'technology.m-press.operation.bodyOne',
      'technology.m-press.operation.bodyTwo',
    ],
  },
  specifications: [
    { labelKey: 'technology.m-press.specifications.programsLabel', valueKey: 'technology.m-press.specifications.programsValue' },
    { labelKey: 'technology.m-press.specifications.sectorsLabel', valueKey: 'technology.m-press.specifications.sectorsValue' },
    { labelKey: 'technology.m-press.specifications.modesLabel', valueKey: 'technology.m-press.specifications.modesValue' },
    { labelKey: 'technology.m-press.specifications.pressureLabel', valueKey: 'technology.m-press.specifications.pressureValue' },
    { labelKey: 'technology.m-press.specifications.displayLabel', valueKey: 'technology.m-press.specifications.displayValue' },
    { labelKey: 'technology.m-press.specifications.durationLabel', valueKey: 'technology.m-press.specifications.durationValue' },
  ],
  related: ['m-body-s', 'm-rf-synergy'],
  contactInterest: 'm-press',
}

export const mShock: TechnologyPageRecord = {
  route: 'm-shock',
  image: { src: '/media/technologies/m-shock.webp', width: 480, height: 1080, altKey: 'technology.m-shock.imageAlt' },
  hero: { titleKey: 'technology.m-shock.hero.title', bodyKeys: ['technology.m-shock.hero.body'] },
  introduction: {
    titleKey: 'technology.m-shock.introduction.title',
    bodyKeys: ['technology.m-shock.introduction.bodyOne', 'technology.m-shock.introduction.bodyTwo'],
  },
  applications: {
    titleKey: 'technology.m-shock.applications.title',
    bodyKeys: ['technology.m-shock.applications.body'],
    itemKeys: ['technology.m-shock.applications.cellulite', 'technology.m-shock.applications.silhouette'],
  },
  operation: {
    titleKey: 'technology.m-shock.operation.title',
    bodyKeys: ['technology.m-shock.operation.bodyOne', 'technology.m-shock.operation.bodyTwo'],
  },
  specifications: [
    { labelKey: 'technology.m-shock.specifications.frequencyLabel', valueKey: 'technology.m-shock.specifications.frequencyValue' },
    { labelKey: 'technology.m-shock.specifications.headsLabel', valueKey: 'technology.m-shock.specifications.headsValue' },
    { labelKey: 'technology.m-shock.specifications.displayLabel', valueKey: 'technology.m-shock.specifications.displayValue' },
    { labelKey: 'technology.m-shock.specifications.dimensionsLabel', valueKey: 'technology.m-shock.specifications.dimensionsValue' },
    { labelKey: 'technology.m-shock.specifications.weightLabel', valueKey: 'technology.m-shock.specifications.weightValue' },
    { labelKey: 'technology.m-shock.specifications.durationLabel', valueKey: 'technology.m-shock.specifications.durationValue' },
  ],
  related: ['m-press', 'm-body-s'],
  contactInterest: 'm-shock',
}

export const mRfSynergy: TechnologyPageRecord = {
  route: 'm-rf-synergy',
  image: {
    src: '/media/technologies/m-rf-synergy.webp',
    width: 480,
    height: 1122,
    altKey: 'technology.m-rf-synergy.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-rf-synergy.hero.title',
    bodyKeys: ['technology.m-rf-synergy.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-rf-synergy.introduction.title',
    bodyKeys: [
      'technology.m-rf-synergy.introduction.bodyOne',
      'technology.m-rf-synergy.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-rf-synergy.applications.title',
    bodyKeys: ['technology.m-rf-synergy.applications.body'],
    itemKeys: [
      'technology.m-rf-synergy.applications.skinAnalyzer',
      'technology.m-rf-synergy.applications.microdermabrasion',
      'technology.m-rf-synergy.applications.peelingCavitation',
      'technology.m-rf-synergy.applications.radiofrequencyLaser',
      'technology.m-rf-synergy.applications.ultrasoundBodyTreatment',
      'technology.m-rf-synergy.applications.dermomassage',
      'technology.m-rf-synergy.applications.needleFreeMesotherapy',
      'technology.m-rf-synergy.applications.ultrasound',
    ],
  },
  operation: {
    titleKey: 'technology.m-rf-synergy.operation.title',
    bodyKeys: [
      'technology.m-rf-synergy.operation.bodyOne',
      'technology.m-rf-synergy.operation.bodyTwo',
    ],
  },
  related: ['m-synergy-a', 'm-shock'],
  contactInterest: 'm-rf-synergy',
}

export const mSynergyA: TechnologyPageRecord = {
  route: 'm-synergy-a',
  image: {
    src: '/media/technologies/m-synergy-a.webp',
    width: 480,
    height: 1099,
    altKey: 'technology.m-synergy-a.imageAlt',
  },
  hero: {
    titleKey: 'technology.m-synergy-a.hero.title',
    bodyKeys: ['technology.m-synergy-a.hero.body'],
  },
  introduction: {
    titleKey: 'technology.m-synergy-a.introduction.title',
    bodyKeys: [
      'technology.m-synergy-a.introduction.bodyOne',
      'technology.m-synergy-a.introduction.bodyTwo',
    ],
  },
  applications: {
    titleKey: 'technology.m-synergy-a.applications.title',
    bodyKeys: ['technology.m-synergy-a.applications.body'],
    itemKeys: [
      'technology.m-synergy-a.applications.antiAge',
      'technology.m-synergy-a.applications.oilySkin',
      'technology.m-synergy-a.applications.sensitiveSkin',
    ],
  },
  operation: {
    titleKey: 'technology.m-synergy-a.operation.title',
    bodyKeys: [
      'technology.m-synergy-a.operation.bodyOne',
      'technology.m-synergy-a.operation.bodyTwo',
    ],
  },
  support: {
    titleKey: 'technology.m-synergy-a.support.title',
    bodyKeys: ['technology.m-synergy-a.support.body'],
  },
  contactInterest: 'm-synergy-a',
}
